import React, { createContext, useContext, useEffect, useState } from 'react'
import { Button, Center, Heading, Text, VStack } from '@chakra-ui/react'
import { firebase, db, app } from '../utils/init-firebase';
import { useHistory } from 'react-router-dom';
import { updateDoc, doc, setDoc, getDoc, onSnapshot, query, collection, orderBy, where, getDocs, arrayUnion, limit } from "firebase/firestore"
import { getAuth } from 'firebase/auth';
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  updateProfile,
} from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions';
import Cookies from 'js-cookie';
import { requestForToken } from '../utils/init-firebase';
import { clearAllViewSessions } from '../utils/crmViewSession';
import LoadingPage from '../pages/LoadingPage';
import { supabase } from '../utils/supabase';
import { pickGooglePhotoURL } from '../utils/googlePhoto';
import { shouldAutoEnrollCrm } from '../utils/crmAutoEnroll';

const AuthContext = createContext({
  users: null,
  eosUsers: null,
  marketingAdmins: null,
  brokers: null,
  marketingSettings: null,
  submissionSettings: null,
  currentUser: null,
  notifications: null,
  Roles: null,
  loading: null,
  isFirstRender: null,
  setIsFirstRender: () => { },
  NMLS: null,
  setupQueueCount: null,
  signInWithGoogle: () => Promise,
  logout: () => Promise,
  currentToken: null,
  isAdmin: null,
  SuperAdmin: null,
  MarketingAdmin: null,
  Level1: null,
  swagBudget: null,
  lenders: null,

  newRequests: null,
  inProgressRequests: null,
  inProgressCounter: null,
  unsubscribeMarketingRef: null,
  showNewReqCounter: null,
  showInProgReqCounter: null,

  currentVersion: null,
  latestVersion: null,
  markFeatureAsSeen: () => Promise,
  hasSeenFeature: () => Promise
})

export const useAuth = () => useContext(AuthContext)

/**
 * Shown instead of hanging on the astronaut forever when a CRITICAL boot step
 * (role check / users list) keeps failing after retries — typically a dead or
 * flaky connection on mobile. A reload restarts the whole boot cleanly.
 */
function InitErrorScreen() {
  return (
    <Center minH="100vh" px={6}>
      <VStack spacing={4} textAlign="center" maxW="sm">
        <Heading size="md">We couldn&apos;t load the Hub</Heading>
        <Text color="gray.500">
          Something went wrong while loading your account — usually a shaky
          internet connection. Check your connection and try again.
        </Text>
        <Button colorScheme="green" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </VStack>
    </Center>
  )
}

export default function AuthProvider({ children }) {
  const history = useHistory();
  const auth = getAuth();
  const [currentUser, setCurrentUser] = useState(null)
  const [Roles, setRoles] = useState(null)
  const [isAdmin, setIsAdmin] = useState(null)
  const [SuperAdmin, setSuperAdmin] = useState(null)
  const [MarketingAdmin, setMarketingAdmin] = useState(null)
  const [Level1, setLevel1] = useState(false)
  const [swagBudget, setSwagBudget] = useState(null)
  const [NMLS, setNMLS] = useState(null)
  const [setupQueueCount, setSetupQueueCount] = useState(null)
  const [loading, setLoading] = useState(true)
  // Set when a CRITICAL boot step failed after retries; renders InitErrorScreen
  // instead of the app (and instead of an infinite astronaut).
  const [initError, setInitError] = useState(null)
  const [isFirstRender, setIsFirstRender] = useState(true)
  // Initialize navSize from localStorage, default to 'large'
  const [navSize, setNavSize] = useState(() => {
    const savedNavSize = localStorage.getItem('navSize')
    return savedNavSize && ['large', 'small', 'hidden'].includes(savedNavSize)
      ? savedNavSize
      : 'large'
  })

  // Wrapper function to update navSize and persist to localStorage
  const changeNavSize = (newSize) => {
    if (['large', 'small', 'hidden'].includes(newSize)) {
      setNavSize(newSize)
      localStorage.setItem('navSize', newSize)
    }
  }
  const [notifications, setNotifications] = useState(null)
  const [users, setUsers] = useState(null)
  const [eosUsers, setEosUsers] = useState(null)
  const [marketingAdmins, setMarketingAdmins] = useState(null)
  const [brokers, setBrokers] = useState(null)
  const [marketingSettings, setMarketingSettings] = useState(null)
  const [submissionSettings, setSubmissionSettings] = useState(null)
  const [currentToken, setCurrentToken] = useState(null)
  const [branches, setBranches] = useState(null)
  const [lenders, setLenders] = useState(null)

  const [newRequests, setNewRequests] = useState(null)
  const [inProgressRequests, setInProgressRequests] = useState(null)
  const [inProgressCounter, setInProgressCounter] = useState(null)

  const [currentVersion, setCurrentVersion] = useState(null)
  const [latestVersion, setLasestVersion] = useState(null)

  const [showNewReqCounter, setShowNewReqCounter] = useState(null)
  const [showInProgReqCounter, setShowInProgReqCounter] = useState(null)


  const unsubscribeMarketingRef = React.useRef()
  const unsubscribeSetupQueueCountRef = React.useRef()
  const unsubscribeSubmissionSettingsRef = React.useRef()

  // Track the last UID we initialized for, so that property merges in getRole()
  // (which call setCurrentUser) do NOT re-trigger initUser().
  const lastInitializedUidRef = React.useRef(null)

  // Sync Firebase native token to localStorage to be read synchronously by the Supabase HTTP interceptor
  useEffect(() => {
    const unsubscribeToken = auth.onIdTokenChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          localStorage.setItem('firebaseToken', token);
        } catch (e) {
          console.error("Error refreshing firebaseToken cache:", e);
        }
      } else {
        localStorage.removeItem('firebaseToken');
      }
    });
    return () => {
      unsubscribeToken();
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        setLoading(true)
        setCurrentUser(user)


        // Cross-Subdomain SSO
        try {
          const functionsObj = getFunctions(app, 'us-east1');
          const getSsoToken = httpsCallable(functionsObj, 'getSsoToken');
          const { data } = await getSsoToken();
          let mainDomain = process.env.REACT_APP_MAIN_DOMAIN || `.${window.location.hostname}`;
          // Browsers reject cookies set to a domain starting with '.' if it resolves to a public suffix (like .web.app or .firebaseapp.com)
          if (window.location.hostname.includes('.app') || window.location.hostname.includes('.com')) {
            mainDomain = window.location.hostname;
          }

          Cookies.set('shared_sso_token', data.customToken, {
            domain: mainDomain,
            secure: true,
            sameSite: 'Lax',
            expires: 1 / 24 // 1 hour
          });
        } catch (error) {
          console.error('SSO Token Error:', error);
        }
      }
      else {
        setCurrentUser(null)
        lastInitializedUidRef.current = null
        setInitError(null)
        setLoading(false)

        let mainDomain = process.env.REACT_APP_MAIN_DOMAIN || `.${window.location.hostname}`;
        if (window.location.hostname.includes('.app') || window.location.hostname.includes('.com')) {
          mainDomain = window.location.hostname;
        }
        Cookies.remove('shared_sso_token', { domain: mainDomain });
      }
    })
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!currentUser) return

    // Only run initUser when the actual user (UID) changes, not when
    // getRole() merges Firestore properties onto currentUser.
    if (currentUser.uid === lastInitializedUidRef.current) return
    lastInitializedUidRef.current = currentUser.uid

    initUser()
      .then(() => {
        setInitError(null)
      })
      .catch((err) => {
        console.error('initUser failed — showing error screen instead of hanging:', err)
        setInitError(err)
      })
      .finally(() => {
        // Whatever happened above, NEVER leave the user stranded on the
        // loading astronaut: loading always resolves.
        setLoading(false)
      })
  }, [currentUser])

  useEffect(() => {
    if (currentToken) {
      addToken()
    }
  }, [currentToken])

  /**
   * Retry a critical boot step before giving up — a single transient
   * Firestore "unavailable" on a mobile radio must not strand the user.
   * Backoff between attempts: 1s, then 2s.
   */
  const withRetry = async (fn, label, retries = 2) => {
    let lastErr
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await fn()
      } catch (err) {
        lastErr = err
        console.warn(`${label} failed (attempt ${attempt + 1}/${retries + 1})`, err)
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)))
        }
      }
    }
    throw lastErr
  }

  /**
   * Boot the signed-in user. Previously this was a `new Promise(async ...)`
   * anti-pattern running ~10 SEQUENTIAL awaits: any throw left the promise
   * permanently unsettled, so setLoading(false) never ran and the app hung on
   * the loading astronaut forever (the top mobile complaint).
   *
   * Now: checkRole and getUsers are CRITICAL (roles gate routing; `users` is
   * consumed unguarded across MyDashboard/EOS/SubmissionDrawer/...) — they
   * retry and, if they still fail, surface InitErrorScreen. Everything else
   * runs in one parallel best-effort wave: their consumers are null-guarded
   * or were already asynchronous onSnapshot results, so a failure logs
   * instead of blocking boot.
   */
  const initUser = async () => {
    // Must run first: creates the user doc on first login and loads the
    // roles/admin flags that gate routing. The steps below depend on the doc
    // existing (e.g. syncGooglePhoto writes to it).
    await withRetry(checkRole, 'checkRole')

    const [usersResult, ...bestEffortResults] = await Promise.allSettled([
      withRetry(getUsers, 'getUsers'), // critical — see note above
      syncGooglePhoto(),
      getNotifs(),
      getMarketingSettings(),
      listenForSubmissionSettings(),
      getLenders(),
      getCurrentVersion(),
      listenForLatestVersion(),
    ])

    bestEffortResults.forEach((result) => {
      if (result.status === 'rejected') {
        console.error('Non-critical init step failed:', result.reason)
      }
    })

    // FCM token is fire-and-forget: the useEffect([currentToken]) above calls
    // addToken() whenever the token actually arrives. (The old `await
    // addToken()` here was a verified no-op — currentToken is always null at
    // this point in the boot.)
    requestForToken(setCurrentToken)

    if (usersResult.status === 'rejected') {
      throw usersResult.reason
    }
  }

  const getLenders = async () => {
    const q = query(collection(db, "lenders"));

    const unsubLenders = onSnapshot(q, (querySnapshot) => {
      const lenders = []
      querySnapshot.forEach((doc) => {
        if (!doc.data().inactive) {
          lenders.push(doc.data())
        }
      })
      setLenders(lenders)
    })

    return () => {
      unsubLenders()
    }
  }

  const getCurrentVersion = async () => {
    const docRef = doc(db, "Version", "Version");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      setCurrentVersion(docSnap.data().Latest)
    } else {
      // doc.data() will be undefined in this case
      console.log("No such document!");
    }
    //console.log('current version: ' + currentVersion)


  };

  const listenForLatestVersion = async () => {
    const unsub = onSnapshot(doc(db, "Version", "Version"), (doc) => {
      setLasestVersion(doc.data().Latest)
    });
    //console.log('latest version: ' + latestVersion)
    return () => {
      unsub()
    }
  };

  const checkRole = async () => {

    const snap = await getDoc(doc(db, 'users', currentUser.uid))
    if (!snap.exists()) {
      // Everyone at FHL is a CRM user by default (company accounts only —
      // see crmAutoEnroll.js); the Admin page toggle remains the override to
      // revoke, and revocation sticks because this create only runs when the
      // doc doesn't exist. Mirrors what UserAdminControls writes on grant.
      // CRMRole is deliberately NOT mirrored into Hub `Roles` (see
      // crmRoleMirroring.js — the Hub "Loan Officer" role broke CSC views).
      const autoEnrollCrm = shouldAutoEnrollCrm(currentUser.email)
      await setDoc(doc(db, 'users', currentUser.uid), {
        uid: currentUser.uid,
        name: currentUser.displayName,
        authProvider: "google",
        email: currentUser.email,
        photo: currentUser.photoURL,
        isAdmin: false,
        SuperAdmin: false,
        MarketingAdmin: false,
        Level1: false,
        Roles: [],
        dateJoined: getDate(),
        seenFeaturesByVersion: {},
        // FHL Scheduling is on by default for everyone (the CRM sidebar gate
        // still hides Origin from non-CRM users).
        calSchedulingEnabled: true,
        ...(autoEnrollCrm ? { CRMUser: true, CRMRole: 'Loan Officer' } : {}),
      });
      // Merge the freshly written doc (CRMUser/CRMRole included) onto
      // currentUser via the same path returning logins use, so the Origin
      // nav appears in the very first session without a refresh.
      await getRole(currentUser.uid)
    }
    else {
      await getRole(currentUser.uid)
      /*         await updateDoc(doc(db, 'users', currentUser.uid) , {
                lastLogin: getDate()
              }); */
    }
  }

  /**
   * Keep the user's profile PHOTO fresh from Google on every login.
   *
   * Firebase Auth's top-level `photoURL` is written once at account creation and
   * is never refreshed from Google afterwards, so a newly uploaded Google photo
   * never reaches the Navbar / Profile (which read `currentUser.photoURL`) or the
   * Firestore `photo` mirror (read by user pickers, CRM/EOS avatars). The
   * google.com provider record on the Auth user always has the current photo, so
   * we push it to both places whenever they drift.
   *
   * Photo only: the display NAME is intentionally left to the server-side
   * `syncAuthDisplayName` trigger, which treats the admin-editable Firestore
   * `Name` as the source of truth — syncing the name from Google here would
   * clobber intentional admin renames.
   */
  const syncGooglePhoto = async () => {
    try {
      const fbUser = auth.currentUser
      if (!fbUser) return

      // Choose the user's REAL Google photo. Users can have multiple linked
      // google.com providers; pickGooglePhotoURL skips default/legacy placeholder
      // avatars and only returns a photo when it's unambiguous (never guesses),
      // so we can't clobber a working avatar with a grey placeholder.
      const googlePhoto = pickGooglePhotoURL(fbUser.providerData)

      // Nothing to do if there is no safe photo or it's already in sync.
      if (!googlePhoto || googlePhoto === fbUser.photoURL) return

      // 1) Refresh the Auth top-level photoURL (Navbar / satellite apps).
      await updateProfile(fbUser, { photoURL: googlePhoto })

      // 2) Mirror to Firestore `photo` (user pickers, CRM/EOS avatars, admin lists).
      //    The doc already exists here because checkRole() ran first.
      try {
        await updateDoc(doc(db, 'users', fbUser.uid), { photo: googlePhoto })
      } catch (e) {
        console.error('syncGooglePhoto: Firestore photo update failed', e)
      }

      // 3) Reflect the new photo in React state so the current session updates
      //    without requiring another reload. uid is unchanged, so this does not
      //    re-trigger initUser().
      setCurrentUser((prev) => (prev ? { ...prev, photoURL: googlePhoto } : prev))
    } catch (e) {
      // Never let a photo-sync hiccup block login.
      console.error('syncGooglePhoto failed', e)
    }
  }

  const getNotifs = async () => {
    if (notifications) {
      return
    }
    var today = new Date();
    var thirtyDaysAgo = new Date(new Date().setDate(today.getDate() - 30));
    const q = query(collection(db, "users", currentUser.uid, 'Notifications'),
      orderBy('timestamp', 'desc'),
      where('read', '==', false),
      where('timestamp', '>', thirtyDaysAgo),
      limit(50)
    );
    const unsubNotifications = onSnapshot(q, (querySnapshot) => {
      const notifications = [];
      querySnapshot.forEach((doc) => {
        notifications.push({
          ...doc.data(),
          id: doc.id
        });
      });
      setNotifications(notifications)
    });
    return () => {
      unsubNotifications()
    }
  }

  const getUsers = async () => {
    if (users) {
      return
    }
    const userList = []
    const marketingUserList = []
    const brokerList = []
    const branches = []
    const eosUserList = []
    const querySnapshot = await getDocs(collection(db, "users"));
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      let user = {
        label: userData.name,
        value: userData.uid,
        photoURL: userData.photo,
        email: userData.email,
        NMLS: userData.NMLS ? userData.NMLS : '',
        website: userData.website ? userData.website : '',
        jobTitle: userData.Roles ? userData.Roles[0] ? userData.Roles[0] : '' : '',
        phoneNo: userData.phoneNo ? userData.phoneNo[0] ? userData.phoneNo[0] : '' : '',
        swagBudget: userData['Swag Budget'] ? parseInt(userData['Swag Budget']) : null,
        isDTCLoanOfficer: userData.isDTCLoanOfficer === true,
        Roles: Array.isArray(userData.Roles) ? userData.Roles : [],
      }
      userList.push(user)
      if (userData.MarketingAdmin) {
        marketingUserList.push(user)
      }
      if (user.jobTitle == 'Mortgage Broker' || user.jobTitle == 'Loan Officer') {
        brokerList.push(user)
      }
      if (userData.Branch && !branches.includes(userData.Branch)) {
        branches.push(userData.Branch)
      }

      // Create EOS user object with full user data for EOS module
      if (userData.isEOSUser === true) {
        eosUserList.push({
          id: doc.id,
          uid: userData.uid || doc.id,
          name: userData.name || userData.Name || '',
          email: userData.email || userData.Email || '',
          isEOSUser: true,
          ...userData
        });
      }
    });

    brokerList.sort((a, b) => {
      const nameA = a.label.toUpperCase(); // ignore upper and lowercase
      const nameB = b.label.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    userList.sort((a, b) => {
      const nameA = a.label.toUpperCase(); // ignore upper and lowercase
      const nameB = b.label.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    // Sort EOS users by name
    eosUserList.sort((a, b) => {
      const nameA = (a.name || '').toUpperCase();
      const nameB = (b.name || '').toUpperCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    setUsers(userList)
    setEosUsers(eosUserList)
    setMarketingAdmins(marketingUserList)
    setBrokers(brokerList)
    const branchSelection = []
    for (const branch of branches) {
      branchSelection.push({ label: branch, value: branch })
    }
    setBranches(branchSelection)
  }

  const addToken = async () => {
    if (currentToken) {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ['FCM Tokens']: arrayUnion(currentToken)
      })
    }
  }


  async function getRole(userID) {
    const snap = await getDoc(doc(db, "users", userID))
    if (snap.exists()) {
      const userData = snap.data();
      setRoles(userData.Roles)
      // what a mess
      setNMLS(userData['Google Admin Sync Data'] &&
        userData['Google Admin Sync Data']['customSchemas'] &&
        userData['Google Admin Sync Data']['customSchemas']['Broker_Information'] &&
        userData['Google Admin Sync Data']['customSchemas']['Broker_Information']['NMLS'] ?
        userData['Google Admin Sync Data']['customSchemas']['Broker_Information']['NMLS'] :
        null)

      setIsAdmin(userData.isAdmin ? userData.isAdmin : false)
      setSuperAdmin(userData.SuperAdmin ? userData.SuperAdmin : false)
      setMarketingAdmin(userData.MarketingAdmin ? userData.MarketingAdmin : false)
      setLevel1(userData.Level1 ? userData.Level1 : false)
      setSwagBudget(userData['Swag Budget'] ? userData['Swag Budget'] : null)

      // Merge Firestore user data into currentUser object
      // This allows components to access isEOSUser, canPresentMeetings, CRMUser, etc. via currentUser
      // Only update if values have actually changed to prevent infinite loops
      if (currentUser) {
        const newIsEOSUser = userData.isEOSUser || false;
        const newCanPresentMeetings = userData.canPresentMeetings || false;
        const newSuperAdmin = userData.SuperAdmin || false;
        const newIsAdmin = userData.isAdmin || false;
        const newMarketingAdmin = userData.MarketingAdmin || false;
        const newLevel1 = userData.Level1 || false;
        const newCRMUser = userData.CRMUser || false;
        const newCRMRole = userData.CRMRole || null;
        // External demo (partner test drive) — strictly opt-in flag; false for
        // every existing user where the field doesn't exist. See demoAccess.js.
        const newExternalDemo = userData.externalDemo === true;
        // Agent Studio (/assistants) — invite-only flag, no SuperAdmin bypass.
        // See AgentStudioRoute.jsx and functions/Agents/studioAuth.js.
        const newAgentStudioEnabled = userData.agentStudioEnabled === true;

        // Check if any values have changed before updating to prevent infinite loop
        if (
          currentUser.isEOSUser !== newIsEOSUser ||
          currentUser.canPresentMeetings !== newCanPresentMeetings ||
          currentUser.SuperAdmin !== newSuperAdmin ||
          currentUser.isAdmin !== newIsAdmin ||
          currentUser.MarketingAdmin !== newMarketingAdmin ||
          currentUser.Level1 !== newLevel1 ||
          currentUser.CRMUser !== newCRMUser ||
          currentUser.CRMRole !== newCRMRole ||
          currentUser.externalDemo !== newExternalDemo ||
          currentUser.agentStudioEnabled !== newAgentStudioEnabled
        ) {
          setCurrentUser({
            ...currentUser,
            isEOSUser: newIsEOSUser,
            canPresentMeetings: newCanPresentMeetings,
            SuperAdmin: newSuperAdmin,
            isAdmin: newIsAdmin,
            MarketingAdmin: newMarketingAdmin,
            Level1: newLevel1,
            CRMUser: newCRMUser,
            CRMRole: newCRMRole,
            externalDemo: newExternalDemo,
            agentStudioEnabled: newAgentStudioEnabled,
          });
        }
      }

      if (userData.MarketingAdmin) {
        await getRequests()
      }
      if (userData.isAdmin || userData.SuperAdmin) {
        await getSetupQueueCount()
      }
    }
    else {
      console.log("No such user")
    }
  }

  const getSetupQueueCount = async () => {
    unsubscribeSetupQueueCountRef.current && unsubscribeSetupQueueCountRef.current()
    let q = query(collection(db, "Clients"),
      where(`Status`, '==', 'New'),
    )
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      let sqc = 0
      querySnapshot.forEach((doc) => {
        sqc++
      });
      setSetupQueueCount(sqc)
    });
    unsubscribeSetupQueueCountRef.current = unsubscribe
  }

  const getRequests = async () => {
    unsubscribeMarketingRef.current && unsubscribeMarketingRef.current()
    let q = query(collection(db, "Marketing Requests"),
      orderBy('Status'),
      orderBy('Date Created', 'desc'),
      where(`Status`, '!=', 'Completed'),
    )

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      let newRequests = []
      let inProgressRequests = []
      let inProgressCounter = 0

      querySnapshot.forEach((doc) => {
        let request = {}
        request = doc.data()
        if (request['Status'] == 'New') {
          newRequests.push(request)
        }
        else if (request['Status'] == 'In Progress') {
          inProgressRequests.push(request)
        }
      });
      setNewRequests(newRequests)
      setInProgressRequests(inProgressRequests)

      inProgressRequests.forEach((request) => {
        request['Assignees']?.forEach((assignee) => {
          if (assignee.uid == currentUser.uid) {
            inProgressCounter++
          }
        })
      })
      setInProgressCounter(inProgressCounter)
    });
    unsubscribeMarketingRef.current = unsubscribe
  }

  async function getMarketingSettings() {
    const snap = await getDoc(doc(db, "Marketing Settings", 'yEwRIqrkneqrGnAQhgvs'))
    if (snap.exists()) {
      setMarketingSettings(snap.data())
    }
  }

  const listenForSubmissionSettings = async () => {
    // Unsubscribe previous listener if it exists
    if (unsubscribeSubmissionSettingsRef.current) {
      unsubscribeSubmissionSettingsRef.current();
    }
    const docRef = doc(db, "Settings", "yEwRIqrkneqrGnAQhgvs");
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        setSubmissionSettings(snap.data());
      }
    });
    unsubscribeSubmissionSettingsRef.current = unsubscribe;
  };

  useEffect(() => {
    return () => {
      if (unsubscribeSubmissionSettingsRef.current) {
        unsubscribeSubmissionSettingsRef.current();
      }
    };
  }, []);

  function logout() {
    // sessionStorage is per-tab, so without this the next user to sign in here
    // would inherit the previous user's saved CRM filters, including an owner
    // filter pointing at someone else.
    clearAllViewSessions()
    setCurrentUser(null)
    firebase.auth().signOut()
  }

  function getDate() {
    return new Date();
  }

  /**
   * Mark a feature as seen for the current user and version
   * @param {string} featureId - The feature identifier
   * @param {string} version - The version string (defaults to latestVersion)
   * @returns {Promise<void>}
   */
  const markFeatureAsSeen = async (featureId, version = null) => {
    if (!currentUser) return;

    const targetVersion = version || latestVersion;
    if (!targetVersion) return;

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        [`seenFeaturesByVersion.${targetVersion}`]: arrayUnion(featureId)
      });
    } catch (error) {
      console.error('Error marking feature as seen:', error);
    }
  };

  /**
   * Check if user has seen a feature for a specific version
   * @param {string} featureId - The feature identifier
   * @param {string} version - The version string (defaults to latestVersion)
   * @returns {Promise<boolean>}
   */
  const hasSeenFeature = async (featureId, version = null) => {
    if (!currentUser) return false;

    const targetVersion = version || latestVersion;
    if (!targetVersion) return false;

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const seenFeaturesByVersion = userData.seenFeaturesByVersion || {};
        const seenFeaturesForVersion = seenFeaturesByVersion[targetVersion] || [];
        return seenFeaturesForVersion.includes(featureId);
      }
    } catch (error) {
      console.error('Error checking if feature has been seen:', error);
    }

    return false;
  };

  const signInWithGoogle = async () => {
    var google_provider = new GoogleAuthProvider();
    try {
      const auth = app.auth();
      const res = await auth.signInWithPopup(google_provider);

      return res;

    } catch (err) {
      console.error(err);
      alert(err.message);
    }

  }

  const value = {
    currentUser,
    Roles,
    NMLS,
    setupQueueCount,
    loading,
    isFirstRender,
    setIsFirstRender,
    signInWithGoogle,
    logout,
    navSize,
    changeNavSize,
    notifications,
    users,
    eosUsers,
    marketingAdmins,
    brokers,
    marketingSettings,
    submissionSettings,
    currentToken,
    isAdmin,
    SuperAdmin,
    MarketingAdmin,
    Level1,
    swagBudget,
    branches,
    lenders,

    newRequests,
    inProgressRequests,
    inProgressCounter,

    currentVersion,
    latestVersion,
    markFeatureAsSeen,
    hasSeenFeature
  }
  if (loading) {
    return (
      <LoadingPage />
    )
  }
  if (initError) {
    return <InitErrorScreen />
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}